import { useConfirmLeave, useFormDraft } from './unsaved-changes';
export { SupportCaseSummary } from './SupportCaseSummary';
export {
  UnsavedChangesProvider,
  useUnsavedChanges,
  useConfirmLeave,
  useFormDraft,
} from './unsaved-changes';
import { collaborationOnly } from '@yingbaba/utils';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useId,
  type ReactNode,
  type ButtonHTMLAttributes,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Clock,
  Check,
  CheckCircle2,
  ShieldCheck,
  X,
  LoaderCircle,
  TriangleAlert,
  FolderOpen,
  FileText,
  RefreshCw,
  Film,
  Play,
  Download,
  Eye,
  Mail,
  LockKeyhole,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Images,
} from '@yingbaba/icons';
import {
  api,
  apiOrigin,
  post,
  postIdempotent,
  login,
  weChatWebsiteLogin,
  useSession,
  ApiError,
  downloadPrivateFile,
  privateFileBlob,
  privateFilePreviewURL,
  captureUploadContext,
} from '@yingbaba/api';
import { money, date, tags, safeReturn, siteURL } from '@yingbaba/utils';
import type { Demand, Provider, Resource, Portfolio, StorageFile } from '@yingbaba/types';
import { assetUrl, imageAssets, categoryCover, marketplaceCover } from './assets';
import brandLogo from './assets/yingbaba-logo.svg';
import './brand.css';
import { avatarFallback } from './avatar';
import { validateAvatarFile } from './avatar-image';
import { AvatarEditor } from './avatar-editor';
import { Select, DatePicker, FormFieldContext } from './form-controls';
export { Select, DatePicker } from './form-controls';
export { assetUrl, imageAssets, categoryCover, marketplaceCover } from './assets';
export { useConversationScroll } from './communication-scroll';
export { PortfolioShowcase, PortfolioPreview } from './portfolio-media';
export {
  portfolioCover,
  portfolioImages,
  portfolioVideo,
  portfolioMediaURL,
  portfolioMediaLabel,
  videoDuration,
} from './portfolio';
import { portfolioCover, portfolioMediaLabel, portfolioVideo, videoDuration } from './portfolio';

export function Brand() {
  return (
    <span className="brand brand--official">
      <img
        className="brand-logo"
        src={brandLogo}
        alt="影爸爸"
        width={166}
        height={43}
        decoding="async"
      />
    </span>
  );
}
export function Avatar({
  name,
  src = '',
  size = 36,
  organization = false,
}: {
  name: string;
  src?: string;
  size?: number;
  organization?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const fallback = avatarFallback(name);
  const url = src.startsWith('/api/v1/avatars/')
    ? `${apiOrigin()}${src}`
    : /^https?:\/\//.test(src)
      ? src
      : '';
  return (
    <span
      className={`profile-avatar ${organization ? 'organization-avatar' : ''}`}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(10, Math.round(size * 0.32)),
        backgroundColor: fallback.background,
        color: fallback.color,
        borderRadius: organization ? Math.round(size * 0.24) : undefined,
      }}
    >
      {url && !failed ? (
        <img
          src={url}
          alt={`${fallback.label}头像`}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span role="img" aria-label={`${fallback.label}头像`}>
          {fallback.initials}
        </span>
      )}
    </span>
  );
}

export function AvatarUploader({
  name,
  src,
  path,
  disabled = false,
}: {
  name: string;
  src?: string;
  path: '/account/avatar' | '/admin/auth/account/avatar' | '/organization/avatar';
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const guard = useRef(() => {});
  const [selected, setSelected] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const query = useQueryClient();
  const toast = useToast();
  function change(file?: File) {
    if (!file) return;
    setError(null);
    try {
      validateAvatarFile(file);
      guard.current = captureUploadContext();
      setSelected(file);
    } catch (reason) {
      setError(reason);
    }
  }
  async function upload(file: File) {
    guard.current();
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await api(path, { method: 'POST', body: form });
      await query.invalidateQueries();
      setSelected(null);
      toast('头像已更新');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="avatar-settings">
      <Avatar name={name} src={src} size={76} organization={path === '/organization/avatar'} />
      <div className="grow stack">
        <div className="row wrap">
          <Button
            type="button"
            variant="secondary"
            loading={busy}
            disabled={disabled || busy}
            onClick={() => input.current?.click()}
          >
            更换头像
          </Button>
        </div>
        <p className="muted small">
          JPEG、PNG、WebP 或 GIF，最大 5 MB；确认裁剪后上传，头像将在资料与协作中公开展示。
        </p>
        <ErrorNotice error={error} />
      </div>
      <Modal
        open={!!selected}
        onOpenChange={(open) => {
          if (!open && !busy) setSelected(null);
        }}
        title="调整头像"
        description="拖动图片调整位置，确认后上传正方形头像。透明区域会使用白色背景。"
      >
        {selected && (
          <AvatarEditor
            key={selected.name + selected.lastModified}
            file={selected}
            busy={busy}
            onSave={upload}
          />
        )}
      </Modal>
      <input
        ref={input}
        type="file"
        className="visually-hidden"
        accept="image/jpeg,image/png,image/webp,image/gif"
        disabled={disabled || busy}
        aria-label="选择头像图片"
        onChange={(event) => {
          void change(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </div>
  );
}
export function Button({
  variant = 'primary',
  size,
  loading,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: string;
  size?: string;
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={`btn btn-${variant} ${size === 'sm' ? 'btn-sm' : ''} ${className}`}
    >
      {loading && <LoaderCircle size={16} className="spinner" />}
      {children}
    </button>
  );
}
export function ButtonLink({
  to,
  children,
  variant = 'primary',
  className = '',
}: {
  to: string;
  children: ReactNode;
  variant?: string;
  className?: string;
}) {
  return /^https?:/.test(to) ? (
    <a className={`btn btn-${variant} ${className}`} href={to}>
      {children}
    </a>
  ) : (
    <Link className={`btn btn-${variant} ${className}`} to={to}>
      {children}
    </Link>
  );
}
export function IconButton({
  label,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...props}
      className={`icon-btn ${props.className || ''}`}
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}
const statuses: Record<string, [string, string]> = {
  draft: ['草稿', ''],
  pending_review: ['待审核', 'orange'],
  published: ['已发布', 'blue'],
  verified: ['已审核', 'green'],
  rejected: ['未通过', 'red'],
  paused: ['已暂停', 'orange'],
  closed: ['已关闭', ''],
  converted: ['已成交', 'green'],
  submitted: ['已提交', 'blue'],
  withdrawn: ['已撤回', ''],
  selected: ['已中标', 'green'],
  preparing: ['筹备中', 'orange'],
  active: ['制作中', 'blue'],
  revision: ['修改中', 'orange'],
  acceptance: ['待验收', 'orange'],
  completed: ['已完成', 'green'],
  invited: ['已邀请', 'orange'],
  interested: ['已报名', 'blue'],
  qualification_submitted: ['初步方案已提交', 'blue'],
  shortlisted: ['已入围', 'green'],
  final_submitted: ['最终方案已提交', 'blue'],
  archived: ['已下架', ''],
  pending_payment_plan: ['待确认付款计划', 'orange'],
  pending_confirmation: ['待双方确认', 'orange'],
  pending_contract: ['待签约', 'orange'],
  pending_initial_payment: ['待启动款', 'orange'],
  pending_payment: ['待付款', 'orange'],
  due: ['待支付', 'orange'],
  not_due: ['等待付款阶段', ''],
  settlement_ready: ['待结算', 'orange'],
  superseded: ['历史版本', ''],
  payment_submitted: ['付款待核验', 'blue'],
  pending_final_payment: ['待尾款', 'orange'],
  in_fulfillment: ['履约中', 'blue'],
  pending_acceptance: ['待验收', 'orange'],
  eligible: ['可结算', 'orange'],
  settled: ['已结算', 'green'],
  pending: ['待处理', 'orange'],
  pending_counterparty: ['待对方处理', 'orange'],
  pending_finance: ['待财务处理', 'orange'],
  processing: ['处理中', 'blue'],
  refund_pending: ['待退款', 'orange'],
  refund_hold: ['退款处理中，暂缓分账', 'orange'],
  resumed: ['已恢复履约', 'green'],
  retained: ['资料留存中', 'blue'],
  closing: ['注销冷静期', 'orange'],
  inactive: ['已停用', ''],
  deletion_requested: ['待删除', 'orange'],
  blocked: ['等待义务结清', 'orange'],
  refund_rejected: ['退款未通过', 'red'],
  confirmed: ['已确认', 'green'],
  accepted: ['已验收', 'green'],
  cancelled: ['已取消', ''],
  declined: ['已拒绝', 'red'],
  expired: ['已过期', ''],
  resolved: ['已解决', 'green'],
  revision_requested: ['待修改', 'orange'],
  ready: ['可下载', 'green'],
  queued: ['排队中', 'orange'],
  running: ['生成中', 'blue'],
  succeeded: ['已生成', 'green'],
  failed: ['失败', 'red'],
  scheduled: ['待发布', 'blue'],
  replied: ['已回复', 'green'],
  enqueued: ['已入队', 'blue'],
  processed: ['已处理', 'green'],
};
export function Status({ value, label }: { value: string; label?: string }) {
  const s = statuses[value] || [value, ''];
  return <span className={`status ${s[1] ? 'status-' + s[1] : ''}`}>{label || s[0]}</span>;
}
export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>;
}
export function TagList({ value }: { value?: string }) {
  return (
    <div className="tags">
      {tags(value)
        .slice(0, 4)
        .map((t) => (
          <Tag key={t}>{t}</Tag>
        ))}
    </div>
  );
}
export function Sample({ show }: { show?: boolean }) {
  return show ? <span className="sample-label">示例</span> : null;
}
export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  const fieldId = useId();
  return (
    <div className="field">
      <label id={`${fieldId}-label`} htmlFor={htmlFor}>
        {label}
      </label>
      <FormFieldContext.Provider
        value={{
          labelId: `${fieldId}-label`,
          descriptionId:
            [hint && `${fieldId}-hint`, error && `${fieldId}-error`].filter(Boolean).join(' ') ||
            undefined,
          invalid: !!error,
        }}
      >
        {children}
      </FormFieldContext.Provider>
      {hint && <small id={`${fieldId}-hint`}>{hint}</small>}
      {error && (
        <span id={`${fieldId}-error`} className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
export function CommunicationConnectionNotice({ state }: { state?: string }) {
  const messages: Record<string, string> = {
    reconnecting: '正在重新连接，恢复后将自动同步消息。',
    offline: '网络已断开，恢复连接后会自动同步消息。',
    unavailable: '实时连接已暂停，请刷新页面重试或重新登录。',
  };
  const message = messages[state || ''];
  return message ? (
    <p className="small muted" role="status">
      {message}
    </p>
  ) : null;
}

export function Notice({ children, tone = '' }: { children: ReactNode; tone?: string }) {
  return (
    <div className={`notice ${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <TriangleAlert size={17} />
      <div>{children}</div>
    </div>
  );
}
export function ErrorNotice({ error }: { error: unknown }) {
  return error ? (
    <Notice tone="error">
      {error instanceof Error ? error.message : '发生错误，请稍后重试'}
      {error instanceof ApiError && error.requestId && (
        <div className="small">问题编号：{error.requestId}</div>
      )}
    </Notice>
  ) : null;
}
export function Empty({
  title = '暂时没有内容',
  description = '新的内容会出现在这里。',
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="icon-tile">
        <FolderOpen size={23} />
      </span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}
export function Loading() {
  return (
    <div className="grid-3" aria-label="正在加载" role="status">
      {[1, 2, 3].map((n) => (
        <div key={n} className="skeleton" />
      ))}
    </div>
  );
}
export function QueryState({
  query,
  children,
  empty = false,
}: {
  query: { isPending: boolean; error: unknown; refetch: () => unknown };
  children: ReactNode;
  empty?: boolean;
}) {
  if (query.isPending) return <Loading />;
  if (query.error)
    return (
      <div className="stack">
        <ErrorNotice error={query.error} />
        <Button variant="secondary" onClick={() => query.refetch()}>
          <RefreshCw size={15} />
          重新加载
        </Button>
      </div>
    );
  if (empty) return <Empty />;
  return <>{children}</>;
}
export function SectionHead({
  eyebrow,
  title,
  description,
  to,
  linkLabel = '查看全部',
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  to?: string;
  linkLabel?: string;
}) {
  return (
    <div className="section-head">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {to && (
        <Link to={to} className="link">
          {linkLabel}
          <ArrowRight size={15} />
        </Link>
      )}
    </div>
  );
}
export function PageTitle({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-title">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}
export function Image({
  src,
  fallbackSrc = imageAssets.studioSmall,
  alt,
  className = '',
  eager = false,
}: {
  src?: string;
  fallbackSrc?: string | null;
  alt: string;
  className?: string;
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const fallback = fallbackSrc === null ? '' : assetUrl(fallbackSrc);
  const resolvedSrc = src ? assetUrl(src) : fallback;
  if (!fallback && (failed || !resolvedSrc))
    return (
      <span
        className={`image-unavailable ${className}`}
        role="img"
        aria-label={`${alt || '作品图片'}未能加载`}
      >
        图片暂不可用
      </span>
    );
  return (
    <img
      className={className}
      src={resolvedSrc}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={(e) => {
        if (!fallback) {
          setFailed(true);
          return;
        }
        if (e.currentTarget.getAttribute('src') !== fallback) e.currentTarget.src = fallback;
      }}
    />
  );
}
export function ProviderCard({ item }: { item: Provider }) {
  const rating = item.rating_tenths > 0 ? (item.rating_tenths / 10).toFixed(1) : '';
  return (
    <Link to={`/providers/${item.id}`} className="media-card">
      <div className="media-wrap">
        <Image
          src={marketplaceCover('providers', item)}
          fallbackSrc={categoryCover('providers', item.category)}
          alt={`${item.name}创作方向`}
        />
        <Tag>{item.category}</Tag>
      </div>
      <div className="media-body">
        <div className="row">
          <Avatar name={item.name} src={item.avatar_url} size={40} organization />
          <div style={{ flex: 1 }}>
            <div className="row" style={{ gap: 6 }}>
              <h3>{item.name}</h3>
              <ShieldCheck size={15} color="#64835c" />
            </div>
            <span className="small muted">{item.city} · 影视创作团队</span>
          </div>
          <Sample show={item.is_sample} />
        </div>
        <p>{item.summary}</p>
        <TagList value={item.tags} />
        <div className="market-signals provider-signals">
          <span>
            <CheckCircle2 size={13} />
            {!collaborationOnly && item.completed_projects > 0
              ? `${item.completed_projects} 个已完成项目`
              : '平台认证团队'}
          </span>
          {!collaborationOnly && item.review_count > 0 && (
            <span>
              {rating} 分 · {item.review_count} 条成交评价
            </span>
          )}
        </div>
        <div className="media-meta">
          {!collaborationOnly && (
            <span>
              项目起步价 <strong className="text-accent">{money(item.starting_price_minor)}</strong>
            </span>
          )}
          <span className="link">
            了解团队
            <ArrowUpRight size={15} />
          </span>
        </div>
      </div>
    </Link>
  );
}
export function DemandCard({ item, base = '/demands' }: { item: Demand; base?: string }) {
  return (
    <article className="demand-card">
      <div className="row spread">
        <Status value={item.status} />
        <Sample show={item.is_sample} />
      </div>
      <Link to={`${base}/${item.id}`}>
        <h3>{item.title}</h3>
      </Link>
      <div className="demand-price">
        {money(item.budget_min_minor)} <span style={{ fontSize: 15, fontWeight: 400 }}>–</span>{' '}
        {money(item.budget_max_minor)}
      </div>
      <div className="demand-info">
        <span>
          <MapPin size={13} />
          {item.city}
        </span>
        <span>
          <Clock size={13} />
          {item.duration || '周期协商'}
        </span>
      </div>
      <TagList value={item.tags || item.category} />
      <div className="market-signals demand-signals">
        <span>
          <ShieldCheck size={13} />
          {item.client_name || '主体已认证'}
        </span>
        <span>
          <CheckCircle2 size={13} />
          {collaborationOnly ? '合作沟通' : '平台交易'}
        </span>
        {!collaborationOnly && item.client_review_count > 0 && (
          <span>
            客户信用 {(item.client_rating_tenths / 10).toFixed(1)} · {item.client_review_count}{' '}
            条评价
          </span>
        )}
        {!collaborationOnly && item.client_payment_sample_count >= 3 && (
          <span>付款及时 {Math.round(item.client_on_time_payment_rate_bps / 100)}%</span>
        )}
      </div>
      <div className="demand-foot">
        <span className="small muted">
          {item.candidate_count > 0 ? `${item.candidate_count} 家团队参与 · ` : ''}
          {date(item.deadline)}交付
        </span>
        <ButtonLink to={`${base}/${item.id}`} variant="secondary" className="btn-sm">
          查看需求
          <ArrowRight size={13} />
        </ButtonLink>
      </div>
    </article>
  );
}
export function ResourceCard({ item }: { item: Resource }) {
  return (
    <Link to={`/resources/${item.id}`} className="media-card">
      <div className="media-wrap">
        <Image
          src={marketplaceCover('resources', item)}
          fallbackSrc={categoryCover('resources', item.category)}
          alt={item.title}
        />
        <Tag>{item.category}</Tag>
      </div>
      <div className="media-body">
        <div className="row spread">
          <h3>{item.title}</h3>
          <Sample show={item.is_sample} />
        </div>
        <p className="row">
          <MapPin size={13} />
          {item.city} · 联系资源方了解详情
        </p>
        <div className="row spread">
          {!collaborationOnly && (
            <strong className="text-accent">
              {money(item.price_minor)} <small className="muted">/ {item.unit}</small>
            </strong>
          )}
          <span className="link">
            查看资源
            <ArrowUpRight size={15} />
          </span>
        </div>
      </div>
    </Link>
  );
}
export function CaseCard({ item }: { item: Portfolio }) {
  const video = portfolioVideo(item);
  return (
    <Link to={`/cases/${item.id}`} className="media-card case-card">
      <div className="media-wrap">
        <Image
          src={portfolioCover(item)}
          fallbackSrc={categoryCover('cases', item.category)}
          alt={item.title}
        />
        <Tag>{item.industry}</Tag>
        <span className="case-format">
          {video?.kind === 'video' ? <Play size={12} fill="currentColor" /> : <Images size={12} />}
          {portfolioMediaLabel(item)}
          {video?.duration ? ` · ${videoDuration(video.duration)}` : ''}
        </span>
        {video?.kind === 'video' && (
          <span className="play-overlay">
            <Play size={18} fill="currentColor" />
          </span>
        )}
      </div>
      <div className="media-body">
        <div className="row spread">
          <h3>{item.title}</h3>
          <Sample show={item.is_sample} />
        </div>
        <p>
          {item.category}
          {item.provider_name ? ` · ${item.provider_name}` : ' · 创作与灵感'}
        </p>
        <TagList value={item.tags} />
        {!!item.credits?.filter((credit) => credit.role !== '制作团队').length && (
          <div className="case-credit-line">
            {item.credits
              .filter((credit) => credit.role !== '制作团队')
              .slice(0, 2)
              .map((credit) => `${credit.role} · ${credit.display_name}`)
              .join('　')}
          </div>
        )}
      </div>
    </Link>
  );
}
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  className = '',
  footer,
  busy = false,
  returnFocusRef,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  className?: string;
  footer?: ReactNode;
  busy?: boolean;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const opener = useRef<HTMLElement | null>(null);
  const content = useRef<HTMLDivElement>(null);
  const descriptionID = useId();
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!busy || next) onOpenChange(next);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay" />
        <DialogPrimitive.Content
          ref={content}
          className={`dialog ${className}`.trim()}
          aria-describedby={description ? descriptionID : undefined}
          aria-busy={busy || undefined}
          onEscapeKeyDown={(event) => {
            // Close the active selector or calendar before dismissing its parent form.
            if (busy || content.current?.querySelector('.control-popover')) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (busy) event.preventDefault();
          }}
          onOpenAutoFocus={() => {
            opener.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const previous = returnFocusRef?.current || opener.current;
            if (previous?.isConnected && previous !== document.body) {
              previous.focus({ preventScroll: true });
            } else {
              // Status changes can remove the action that opened the dialog.
              // Give keyboard users a stable context without jumping the page.
              const context =
                document.querySelector<HTMLElement>('main h1') ||
                document.querySelector<HTMLElement>('main');
              if (context) {
                context.tabIndex = -1;
                context.focus({ preventScroll: true });
              }
            }
          }}
        >
          <div className="dialog-header">
            <DialogPrimitive.Title className="dialog-title">{title}</DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description id={descriptionID} className="dialog-description">
                {description}
              </DialogPrimitive.Description>
            )}
            <DialogPrimitive.Close asChild>
              <IconButton label="关闭" className="dialog-close" disabled={busy}>
                <X size={18} />
              </IconButton>
            </DialogPrimitive.Close>
          </div>
          <div className="dialog-body">{children}</div>
          {footer && <div className="dialog-footer dialog-actions">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function filePreviewKind(mimeType: string) {
  const mime = mimeType.split(';', 1)[0].trim().toLowerCase();
  if (
    ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp', 'image/avif'].includes(mime)
  )
    return 'image';
  if (['video/mp4', 'video/webm', 'video/quicktime'].includes(mime)) return 'video';
  if (
    ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/aac', 'audio/flac', 'audio/ogg'].includes(mime)
  )
    return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  return '';
}

export function compactFileSize(size: number) {
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function privateDocumentKind(mimeType: string) {
  const mime = mimeType.split(';', 1)[0].trim().toLowerCase();
  if (mime === 'image/png' || mime === 'image/jpeg' || mime === 'image/webp') return 'image';
  if (mime === 'application/pdf') return 'pdf';
  return 'file';
}

type MediaPreviewKind = 'image' | 'video' | 'audio' | 'pdf';

const MIN_IMAGE_PREVIEW_SCALE = 0.5;
const MAX_IMAGE_PREVIEW_SCALE = 6;

function clampImagePreviewScale(scale: number) {
  return Math.min(MAX_IMAGE_PREVIEW_SCALE, Math.max(MIN_IMAGE_PREVIEW_SCALE, scale));
}

export function MediaPreviewDialog({
  open,
  onOpenChange,
  title,
  description,
  kind,
  src,
  onDownload,
  downloadLabel = '下载原件',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  kind: MediaPreviewKind;
  src: string;
  onDownload?: () => void | Promise<void>;
  downloadLabel?: string;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageViewRef = useRef({ scale: 1, x: 0, y: 0 });
  const [stageNode, setStageNode] = useState<HTMLDivElement | null>(null);
  const pointerStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const gestureBaseScaleRef = useRef(1);
  const [imageView, setImageView] = useState({ scale: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  useEffect(() => setMediaError(false), [open, src]);
  imageViewRef.current = imageView;

  const setStageRef = useCallback((node: HTMLDivElement | null) => {
    stageRef.current = node;
    setStageNode(node);
  }, []);

  const clampPan = (x: number, y: number, scale: number) => {
    const stage = stageRef.current;
    const image = imageRef.current;
    if (!stage || !image || scale <= 1) return { x: 0, y: 0 };
    const maxX = Math.max(0, (image.offsetWidth * scale - stage.clientWidth) / 2 + 24);
    const maxY = Math.max(0, (image.offsetHeight * scale - stage.clientHeight) / 2 + 24);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  const zoomTo = (requestedScale: number, clientX?: number, clientY?: number) => {
    setImageView((current) => {
      const scale = clampImagePreviewScale(requestedScale);
      if (Math.abs(scale - current.scale) < 0.001) return current;
      const stage = stageRef.current;
      if (!stage || clientX === undefined || clientY === undefined) {
        const nextPan = clampPan(current.x, current.y, scale);
        return { scale, ...nextPan };
      }
      const rect = stage.getBoundingClientRect();
      const pivotX = clientX - (rect.left + rect.width / 2);
      const pivotY = clientY - (rect.top + rect.height / 2);
      const ratio = scale / current.scale;
      const nextPan = clampPan(
        pivotX - (pivotX - current.x) * ratio,
        pivotY - (pivotY - current.y) * ratio,
        scale,
      );
      return { scale, ...nextPan };
    });
  };

  const zoomBy = (factor: number, clientX?: number, clientY?: number) => {
    setImageView((current) => {
      const scale = clampImagePreviewScale(current.scale * factor);
      if (Math.abs(scale - current.scale) < 0.001) return current;
      const stage = stageRef.current;
      if (!stage || clientX === undefined || clientY === undefined) {
        const nextPan = clampPan(current.x, current.y, scale);
        return { scale, ...nextPan };
      }
      const rect = stage.getBoundingClientRect();
      const pivotX = clientX - (rect.left + rect.width / 2);
      const pivotY = clientY - (rect.top + rect.height / 2);
      const ratio = scale / current.scale;
      const nextPan = clampPan(
        pivotX - (pivotX - current.x) * ratio,
        pivotY - (pivotY - current.y) * ratio,
        scale,
      );
      return { scale, ...nextPan };
    });
  };

  const resetImageView = () => {
    pointerStateRef.current = null;
    setDragging(false);
    setImageView({ scale: 1, x: 0, y: 0 });
  };

  useEffect(() => {
    if (!open || kind !== 'image') return;
    resetImageView();
  }, [open, kind, src]);

  useEffect(() => {
    if (!open || kind !== 'image' || !stageNode) return;
    const stage = stageNode;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY;
      const speed = event.ctrlKey ? 0.01 : 0.0018;
      zoomBy(Math.exp(-delta * speed), event.clientX, event.clientY);
    };

    let gestureStartScale = 1;
    const onGestureStart = (event: Event) => {
      event.preventDefault();
      gestureStartScale = imageViewRef.current.scale;
      gestureBaseScaleRef.current = gestureStartScale;
    };
    const onGestureChange = (event: Event) => {
      event.preventDefault();
      const gesture = event as Event & { scale?: number; clientX?: number; clientY?: number };
      const factor = Number.isFinite(gesture.scale) ? Number(gesture.scale) : 1;
      zoomTo(gestureBaseScaleRef.current * factor, gesture.clientX, gesture.clientY);
    };

    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener('gesturestart', onGestureStart, {
      passive: false,
    } as AddEventListenerOptions);
    stage.addEventListener('gesturechange', onGestureChange, {
      passive: false,
    } as AddEventListenerOptions);
    return () => {
      stage.removeEventListener('wheel', onWheel);
      stage.removeEventListener('gesturestart', onGestureStart);
      stage.removeEventListener('gesturechange', onGestureChange);
    };
  }, [open, kind, stageNode]);

  const imageScalePercent = Math.round(imageView.scale * 100);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay media-preview-overlay" />
        <DialogPrimitive.Content
          className="media-preview-dialog"
          onKeyDown={(event) => {
            if (kind !== 'image') return;
            if (event.key === '+' || event.key === '=') {
              event.preventDefault();
              zoomBy(1.25);
            } else if (event.key === '-') {
              event.preventDefault();
              zoomBy(0.8);
            } else if (event.key === '0') {
              event.preventDefault();
              resetImageView();
            }
          }}
        >
          <div className="media-preview-header">
            <div className="media-preview-meta">
              <DialogPrimitive.Title className="media-preview-title">{title}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="media-preview-description">
                {description || '在线预览'}
                {kind === 'image' && (
                  <span className="media-preview-gesture-hint"> · 滚轮/双指缩放 · 拖拽移动</span>
                )}
              </DialogPrimitive.Description>
            </div>
            <div className="media-preview-actions">
              {kind === 'image' && (
                <div className="media-preview-zoom-controls" aria-label="图片缩放控制">
                  <IconButton
                    type="button"
                    label="缩小图片"
                    disabled={imageView.scale <= MIN_IMAGE_PREVIEW_SCALE + 0.001}
                    onClick={() => zoomBy(0.8)}
                  >
                    <ZoomOut size={20} />
                  </IconButton>
                  <button
                    type="button"
                    className="media-preview-zoom-value"
                    title="重置为适合窗口"
                    aria-label={`当前缩放 ${imageScalePercent}%，点击重置`}
                    onClick={resetImageView}
                  >
                    {imageScalePercent}%
                  </button>
                  <IconButton
                    type="button"
                    label="放大图片"
                    disabled={imageView.scale >= MAX_IMAGE_PREVIEW_SCALE - 0.001}
                    onClick={() => zoomBy(1.25)}
                  >
                    <ZoomIn size={20} />
                  </IconButton>
                  <IconButton
                    type="button"
                    label="重置图片大小"
                    disabled={
                      Math.abs(imageView.scale - 1) < 0.001 &&
                      imageView.x === 0 &&
                      imageView.y === 0
                    }
                    onClick={resetImageView}
                  >
                    <RotateCcw size={19} />
                  </IconButton>
                </div>
              )}
              {onDownload && (
                <IconButton type="button" label={downloadLabel} onClick={() => void onDownload()}>
                  <Download size={20} />
                </IconButton>
              )}
              <DialogPrimitive.Close asChild>
                <IconButton type="button" label="关闭预览">
                  <X size={20} />
                </IconButton>
              </DialogPrimitive.Close>
            </div>
          </div>
          <div
            ref={setStageRef}
            className={`media-preview-stage ${kind} ${kind === 'image' && imageView.scale > 1 ? 'is-zoomed' : ''} ${dragging ? 'is-dragging' : ''}`}
            onDoubleClick={(event) => {
              if (kind !== 'image') return;
              if (imageView.scale > 1.05) resetImageView();
              else zoomTo(2, event.clientX, event.clientY);
            }}
            onPointerDown={(event) => {
              if (kind !== 'image' || event.button !== 0 || imageView.scale <= 1) return;
              pointerStateRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                originX: imageView.x,
                originY: imageView.y,
              };
              event.currentTarget.setPointerCapture(event.pointerId);
              setDragging(true);
            }}
            onPointerMove={(event) => {
              const pointer = pointerStateRef.current;
              if (!pointer || pointer.pointerId !== event.pointerId) return;
              const nextPan = clampPan(
                pointer.originX + event.clientX - pointer.startX,
                pointer.originY + event.clientY - pointer.startY,
                imageView.scale,
              );
              setImageView((current) => ({ ...current, ...nextPan }));
            }}
            onPointerUp={(event) => {
              const pointer = pointerStateRef.current;
              if (!pointer || pointer.pointerId !== event.pointerId) return;
              pointerStateRef.current = null;
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
              setDragging(false);
            }}
            onPointerCancel={() => {
              pointerStateRef.current = null;
              setDragging(false);
            }}
          >
            {kind === 'image' ? (
              <img
                ref={imageRef}
                className="media-preview-image"
                src={src}
                alt={title}
                draggable={false}
                onError={() => setMediaError(true)}
                style={{
                  transform: `translate3d(${imageView.x}px, ${imageView.y}px, 0) scale(${imageView.scale})`,
                }}
              />
            ) : kind === 'video' ? (
              <video
                src={src}
                controls
                playsInline
                preload="metadata"
                onError={() => setMediaError(true)}
              />
            ) : kind === 'audio' ? (
              <audio src={src} controls preload="metadata" onError={() => setMediaError(true)} />
            ) : (
              <iframe src={src} title={`预览 ${title}`} />
            )}
            {mediaError && (
              <p className="media-preview-error" role="alert">
                当前浏览器无法显示此文件，或访问权限已变化。可下载原文件后使用对应软件打开。
              </p>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function PrivateDocumentPreview({
  path,
  fileName,
  mimeType,
  size,
  label,
  downloadPath = path,
}: {
  path: string;
  fileName: string;
  mimeType: string;
  size: number;
  label?: string;
  downloadPath?: string;
}) {
  const [open, setOpen] = useState(false);
  const [objectURL, setObjectURL] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const toast = useToast();
  const kind = privateDocumentKind(mimeType);

  useEffect(() => {
    let disposed = false;
    let currentURL = '';
    setLoading(true);
    setError('');
    setObjectURL('');
    void privateFileBlob(path)
      .then((blob) => {
        if (disposed) return;
        currentURL = URL.createObjectURL(blob);
        setObjectURL(currentURL);
      })
      .catch((reason) => {
        if (!disposed) setError(reason instanceof Error ? reason.message : '材料预览失败');
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });
    return () => {
      disposed = true;
      if (currentURL) URL.revokeObjectURL(currentURL);
    };
  }, [path]);

  const download = async () => {
    try {
      await downloadPrivateFile(downloadPath, fileName);
    } catch (reason) {
      toast(reason instanceof Error ? reason.message : '材料下载失败，请稍后重试');
    }
  };
  const canPreview = Boolean(objectURL) && kind !== 'file';

  return (
    <>
      <div className={`private-document-preview private-document-preview-${kind}`}>
        <div className="private-document-preview-stage">
          {loading ? (
            <span className="private-document-preview-placeholder">
              <LoaderCircle className="spinner" size={22} /> 正在安全读取材料…
            </span>
          ) : error ? (
            <span className="private-document-preview-placeholder error">{error}</span>
          ) : kind === 'image' && objectURL ? (
            <button
              type="button"
              className="private-document-preview-image-button"
              onClick={() => setOpen(true)}
              aria-label={`查看大图：${label || fileName}`}
            >
              <img src={objectURL} alt={label || fileName} loading="lazy" decoding="async" />
              <span className="private-document-preview-zoom">
                <Eye size={16} /> 点击查看大图
              </span>
            </button>
          ) : kind === 'pdf' && objectURL ? (
            <div className="private-document-preview-pdf">
              <iframe src={objectURL} title={`预览 ${label || fileName}`} />
              <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
                <Eye size={15} /> 放大预览 PDF
              </Button>
            </div>
          ) : (
            <span className="private-document-preview-placeholder">
              <FileText size={24} /> 此文件类型请下载后查看
            </span>
          )}
        </div>
        <div className="private-document-preview-footer">
          <div className="private-document-preview-meta">
            {label && <strong>{label}</strong>}
            <span title={fileName}>{fileName}</span>
            <small>{compactFileSize(size)}</small>
          </div>
          <div className="row wrap">
            {canPreview && kind === 'image' && (
              <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
                <Eye size={15} /> 大图
              </Button>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={() => void download()}>
              <Download size={15} /> 下载
            </Button>
          </div>
        </div>
      </div>

      {canPreview && (
        <MediaPreviewDialog
          open={open}
          onOpenChange={setOpen}
          title={label || fileName}
          description={`${fileName} · ${compactFileSize(size)}`}
          kind={kind as 'image' | 'pdf'}
          src={objectURL}
          onDownload={download}
        />
      )}
    </>
  );
}

export function FileAttachment({
  name,
  mime,
  size,
  createdAt,
  previewPath,
  downloadPath,
  withOrganization = true,
}: {
  name: string;
  mime: string;
  size: number;
  createdAt?: string;
  previewPath: string;
  downloadPath: string;
  withOrganization?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pdfURL, setPDFURL] = useState('');
  const [loading, setLoading] = useState(false);
  const kind = filePreviewKind(mime);
  const toast = useToast();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(
    () => () => {
      if (pdfURL) URL.revokeObjectURL(pdfURL);
    },
    [pdfURL],
  );
  const preview = async () => {
    if (kind !== 'pdf') {
      setOpen(true);
      return;
    }
    if (pdfURL) {
      setOpen(true);
      return;
    }
    setLoading(true);
    try {
      const blob = await privateFileBlob(previewPath);
      if (mounted.current) {
        setPDFURL(URL.createObjectURL(blob));
        setOpen(true);
      }
    } catch (error) {
      if (mounted.current) toast(error instanceof Error ? error.message : '文件预览失败');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };
  const download = async () => {
    try {
      await downloadPrivateFile(downloadPath, name);
    } catch (error) {
      toast(error instanceof Error ? error.message : '文件下载失败，请重试');
    }
  };
  return (
    <>
      <div className="list-item project-file-row">
        <span className="icon-tile">
          <FileText size={21} />
        </span>
        <div className="grow project-file-info">
          <strong>{name}</strong>
          <p>
            {compactFileSize(size)}
            {createdAt ? ` · ${new Date(createdAt).toLocaleString('zh-CN')}` : ''}
          </p>
        </div>
        <div className="row">
          {kind && (
            <Button
              variant="secondary"
              size="sm"
              loading={loading}
              onClick={() => void preview()}
              aria-label={`预览 ${name}`}
            >
              <Eye size={16} />
              预览
            </Button>
          )}
          <IconButton label={`下载 ${name}`} onClick={() => void download()}>
            <Download size={17} />
          </IconButton>
        </div>
      </div>
      {kind && (
        <MediaPreviewDialog
          open={open}
          onOpenChange={(value) => {
            setOpen(value);
            if (!value) setPDFURL('');
          }}
          title={name}
          description={compactFileSize(size)}
          kind={kind}
          src={kind === 'pdf' ? pdfURL : privateFilePreviewURL(previewPath, withOrganization)}
          onDownload={download}
          downloadLabel="下载原文件"
        />
      )}
    </>
  );
}

export function CommunicationAttachment({
  file,
  previewPath,
  thumbnailPath,
  downloadPath,
  withOrganization = false,
}: {
  file: StorageFile;
  previewPath: string;
  thumbnailPath?: string;
  downloadPath: string;
  withOrganization?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const toast = useToast();
  const kind = filePreviewKind(file.mime_type);
  const previewURL = kind ? privateFilePreviewURL(previewPath, withOrganization) : '';
  const thumbnailURL =
    kind === 'video' && !thumbnailFailed && thumbnailPath
      ? privateFilePreviewURL(thumbnailPath, withOrganization)
      : '';

  const download = async () => {
    try {
      await downloadPrivateFile(downloadPath, file.file_name);
    } catch (error) {
      toast(error instanceof Error ? error.message : '附件下载失败，请稍后重试');
    }
  };

  if (kind === 'pdf' || kind === 'audio') {
    return (
      <FileAttachment
        name={file.file_name}
        mime={file.mime_type}
        size={file.size}
        previewPath={previewPath}
        downloadPath={downloadPath}
        withOrganization={withOrganization}
      />
    );
  }
  if (!kind) {
    return (
      <button
        className="communication-file"
        type="button"
        onClick={() => void download()}
        title="点击下载文件"
      >
        <FileText size={17} />
        <span className="communication-file-info">
          <strong>{file.file_name}</strong>
          <small>{compactFileSize(file.size)}</small>
        </span>
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`communication-media communication-media-${kind}`}
        onClick={() => setOpen(true)}
        aria-label={`预览 ${file.file_name}`}
      >
        {kind === 'image' ? (
          <img src={previewURL} alt={file.file_name} loading="lazy" decoding="async" />
        ) : (
          <>
            {thumbnailURL ? (
              <img
                src={thumbnailURL}
                alt=""
                loading="lazy"
                decoding="async"
                aria-hidden="true"
                onError={() => setThumbnailFailed(true)}
              />
            ) : (
              <span className="communication-video-placeholder" aria-hidden="true">
                <Film size={34} />
              </span>
            )}
            <span className="communication-media-play" aria-hidden="true">
              <Play size={22} fill="currentColor" />
            </span>
          </>
        )}
      </button>

      <MediaPreviewDialog
        open={open}
        onOpenChange={setOpen}
        title={file.file_name}
        description={compactFileSize(file.size)}
        kind={kind}
        src={previewURL}
        onDownload={download}
        downloadLabel="下载原文件"
      />
    </>
  );
}
type FormField = {
  name: string;
  label: string;
  type?: string;
  options?: string[] | { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  value?: string;
  hint?: string;
  min?: number | string;
  max?: number | string;
  step?: number;
};
type ActionDialogProps = {
  title: string;
  trigger: ReactNode;
  path: string;
  fields?: FormField[];
  transform?: (v: Record<string, string>) => unknown;
  onSuccess?: (data: unknown, context: { leaving: boolean }) => void;
  description?: string;
  variant?: string;
  method?: 'POST' | 'PUT' | 'DELETE';
  idempotent?: boolean;
  protectDraft?: boolean | 'discard-only';
} & (
  | { intent: 'destructive' | 'financial'; submitLabel: string }
  | { intent?: 'standard'; submitLabel?: string }
);
export function ActionDialog({
  title,
  trigger,
  path,
  fields = [],
  transform,
  onSuccess,
  description,
  submitLabel = '提交',
  intent = 'standard',
  variant,
  method = 'POST',
  idempotent = false,
  protectDraft = false,
}: ActionDialogProps) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const idempotencyKey = useRef('');
  const commandFingerprint = useRef('');
  const pending = useRef(false);
  const errorTarget = useRef<HTMLDivElement | null>(null);
  const formID = useId();
  useEffect(() => {
    if (error) errorTarget.current?.scrollIntoView({ block: 'nearest' });
  }, [error]);
  const leave = useConfirmLeave();
  const draft = useFormDraft({
    enabled: open && !!protectDraft,
    busy,
    save: protectDraft === 'discard-only' ? undefined : (form) => persist(form, true),
    title: protectDraft === 'discard-only' ? '离开当前编辑？' : undefined,
    description:
      protectDraft === 'discard-only' ? '资料尚未提交，离开后将放弃当前修改。' : undefined,
    discard: () => setOpen(false),
  });
  function close() {
    if (protectDraft) leave(() => setOpen(false));
    else setOpen(false);
  }
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await persist(e.currentTarget);
  }
  async function persist(form: HTMLFormElement, leaving = false): Promise<boolean> {
    if (pending.current) return false;
    pending.current = true;
    const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
    setBusy(true);
    setError(null);
    try {
      const payload = transform ? transform(v) : v;
      if (idempotent) {
        const fingerprint = JSON.stringify(payload);
        if (fingerprint !== commandFingerprint.current) {
          idempotencyKey.current = crypto.randomUUID();
          commandFingerprint.current = fingerprint;
        }
      }
      const data =
        method !== 'POST'
          ? await api(path, {
              method,
              ...(method === 'PUT' ? { body: JSON.stringify(payload) } : {}),
            })
          : idempotent
            ? await postIdempotent(
                path,
                payload,
                idempotencyKey.current || (idempotencyKey.current = crypto.randomUUID()),
              )
            : await post(path, payload);
      await qc.invalidateQueries();
      draft.clean();
      setOpen(false);
      toast('操作已完成');
      onSuccess?.(data, { leaving });
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        variant={variant || (intent === 'destructive' ? 'danger' : 'primary')}
        size="sm"
        disabled={busy}
        onClick={() => {
          setError(null);
          if (idempotent) {
            idempotencyKey.current = crypto.randomUUID();
            commandFingerprint.current = '';
          }
          setOpen(true);
        }}
      >
        {trigger}
      </Button>
      <Modal
        open={open}
        onOpenChange={(value) => {
          if (!pending.current && !value) close();
        }}
        title={title}
        description={description}
        busy={busy}
        footer={
          <>
            <Button variant="secondary" type="button" disabled={busy} onClick={close}>
              取消
            </Button>
            <Button
              variant={intent === 'destructive' ? 'danger' : 'primary'}
              loading={busy}
              type="submit"
              form={formID}
            >
              {submitLabel}
            </Button>
          </>
        }
      >
        <form
          id={formID}
          ref={draft.ref}
          onChange={draft.onChange}
          onSubmit={submit}
          className="form-stack"
        >
          <fieldset className="form-draft-fields" disabled={busy}>
            {fields.map((f) => (
              <Field key={f.name} htmlFor={`field-${f.name}`} label={f.label} hint={f.hint}>
                {f.options ? (
                  <Select
                    id={`field-${f.name}`}
                    name={f.name}
                    defaultValue={f.value}
                    required={f.required !== false}
                  >
                    {f.options.map((o) => (
                      <option
                        key={typeof o === 'string' ? o : o.value}
                        value={typeof o === 'string' ? o : o.value}
                      >
                        {typeof o === 'string' ? o : o.label}
                      </option>
                    ))}
                  </Select>
                ) : f.type === 'textarea' ? (
                  <textarea
                    id={`field-${f.name}`}
                    name={f.name}
                    defaultValue={f.value}
                    placeholder={f.placeholder}
                    required={f.required !== false}
                    minLength={typeof f.min === 'number' ? f.min : undefined}
                    maxLength={typeof f.max === 'number' ? f.max : 8000}
                  />
                ) : f.type === 'date' || f.type === 'datetime-local' ? (
                  <DatePicker
                    id={`field-${f.name}`}
                    name={f.name}
                    type={f.type}
                    defaultValue={f.value}
                    required={f.required !== false}
                    min={typeof f.min === 'string' ? f.min : undefined}
                    max={typeof f.max === 'string' ? f.max : undefined}
                  />
                ) : (
                  <input
                    id={`field-${f.name}`}
                    className="input"
                    name={f.name}
                    type={f.type || 'text'}
                    defaultValue={f.value}
                    placeholder={f.placeholder}
                    required={f.required !== false}
                    min={f.min}
                    max={f.max}
                    minLength={typeof f.min === 'number' ? f.min : undefined}
                    maxLength={typeof f.max === 'number' ? f.max : undefined}
                    step={f.step}
                  />
                )}
              </Field>
            ))}
          </fieldset>
          {!!error && (
            <div ref={errorTarget}>
              <ErrorNotice error={error} />
            </div>
          )}
        </form>
      </Modal>
    </>
  );
}
const ToastContext = createContext<(s: string) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, set] = useState('');
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => set(''), 3500);
      return () => clearTimeout(t);
    }
  }, [message]);
  return (
    <ToastContext.Provider value={set}>
      {children}
      {message && (
        <div role="status" className="toast">
          <CheckCircle2 size={17} />
          {message}
        </div>
      )}
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);
export class ErrorBoundary extends React.Component<{ children: ReactNode }, { error: boolean }> {
  state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    return this.state.error ? (
      <div className="container section">
        <Empty
          title="页面暂时无法显示"
          description="你的已保存内容不会丢失，请刷新后重试。"
          action={<Button onClick={() => location.reload()}>刷新页面</Button>}
        />
      </div>
    ) : (
      this.props.children
    );
  }
}
const authSchema = z.object({
  email: z.email('请输入有效邮箱'),
  password: z.string().min(10, '密码至少 10 个字符').max(72, '密码过长'),
  mfa_code: z.string().max(32, '验证码或恢复码过长').optional(),
  name: z.string().optional(),
  organization: z.string().optional(),
  capability: z.string().optional(),
});
type AuthInput = z.infer<typeof authSchema>;
export function AuthPage({ admin = false }: { admin?: boolean }) {
  const [params, setParams] = useSearchParams();
  const register = !admin && params.get('mode') === 'register';
  const forgot = !admin && params.get('mode') === 'forgot';
  const [securityMessage, setSecurityMessage] = useState('');
  const [securityBusy, setSecurityBusy] = useState(false);
  const securityPending = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const [wechatEnabled, setWechatEnabled] = useState(false);
  const [wechatOfficial, setWechatOfficial] = useState(false);
  const [qrOpen, setQROpen] = useState(false);
  const [wechatQR, setWechatQR] = useState<{
    qr_code_url: string;
    expires_at: string;
    login_code: string;
  } | null>(null);
  const [qrState, setQRState] = useState('请用微信扫一扫');
  const [qrError, setQRError] = useState<unknown>(null);
  const [qrNow, setQRNow] = useState(Date.now());
  const qrGeneration = useRef(0);
  const [wechatBusy, setWechatBusy] = useState(false);
  const [wechatMFA, setWechatMFA] = useState('');
  const [wechatStatus, setWechatStatus] = useState<{ stage: string; mfa_required: boolean } | null>(
    null,
  );
  const wechatStage = !admin ? params.get('wechat') || '' : '';
  useEffect(() => {
    if (admin || new URL(siteURL('www')).origin !== window.location.origin) return;
    let active = true;
    api<{ wechat_website: boolean; wechat_official?: boolean }>('/auth/methods')
      .then((v) => {
        if (active) {
          setWechatEnabled(v.wechat_website || !!v.wechat_official);
          setWechatOfficial(!!v.wechat_official);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [admin]);
  useEffect(() => {
    if (!['bind', 'complete'].includes(wechatStage)) return;
    let active = true;
    setWechatStatus(null);
    api<{ stage: string; mfa_required: boolean }>('/auth/wechat/status')
      .then((v) => active && setWechatStatus(v))
      .catch((e) => active && setError(e));
    return () => {
      active = false;
    };
  }, [wechatStage]);
  const resetToken = !admin ? params.get('reset_token') || '' : '';
  const verifyToken = !admin ? params.get('verify_token') || '' : '';
  const emailChangeToken = !admin ? params.get('email_change_token') || '' : '';
  const verification = useRef<{ token: string; request: Promise<unknown> } | null>(null);
  const {
    register: field,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AuthInput>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      capability: ['client', 'provider', 'resource'].includes(params.get('role') || '')
        ? params.get('role') || 'client'
        : 'client',
    },
  });
  const target = safeReturn(params.get('returnTo'));
  useEffect(() => {
    if (!qrOpen || !wechatQR) return;
    let active = true;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const clock = setInterval(() => setQRNow(Date.now()), 1000);
    async function poll() {
      if (!active || Date.now() >= new Date(wechatQR!.expires_at).getTime()) return;
      if (!document.hidden) {
        try {
          const state = await api<{ stage: string; mfa_required: boolean }>(
            '/auth/wechat/official/status',
            { signal: controller.signal },
          );
          if (!active) return;
          setQRError(null);
          if (['bind', 'complete'].includes(state.stage)) {
            active = false;
            if (state.stage === 'complete' && !state.mfa_required) {
              setQRState('已确认，正在登录…');
              try {
                await weChatWebsiteLogin('complete', {});
                setQROpen(false);
                await qc.invalidateQueries({ queryKey: ['session'] });
                finishAuth();
              } catch (e) {
                setQRError(e);
                setQRState('登录未完成，请重新扫码');
              }
            } else {
              setQROpen(false);
              const next = new URLSearchParams(params);
              next.delete('mode');
              next.set('wechat', state.stage);
              setParams(next, { replace: true });
            }
            return;
          }
          setQRState(
            state.stage === 'scanned' ? '已扫码，请在微信消息中打开链接确认登录' : '请用微信扫一扫',
          );
        } catch (e) {
          if (!active) return;
          setQRError(e);
          if (e instanceof ApiError && e.code === 'WECHAT_LOGIN_EXPIRED') return;
        }
      }
      if (active) timer = setTimeout(poll, 2000);
    }
    void poll();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
      clearInterval(clock);
    };
  }, [qrOpen, wechatQR]);
  const registrationRole = params.get('role');
  useEffect(() => {
    setValue(
      'capability',
      ['client', 'provider', 'resource'].includes(registrationRole || '')
        ? registrationRole || 'client'
        : 'client',
    );
  }, [registrationRole, setValue]);
  function setMode(mode: 'login' | 'register' | 'forgot') {
    if (qrOpen) closeWechatQR();
    const next = new URLSearchParams(params);
    if (mode === 'login') next.delete('mode');
    else next.set('mode', mode);
    setParams(next);
    setError(null);
    setSecurityMessage('');
  }
  useEffect(() => {
    setError(null);
    setSecurityMessage('');
  }, [params.get('mode')]);
  const destination =
    !admin &&
    typeof window !== 'undefined' &&
    new URL(siteURL('www')).origin === window.location.origin &&
    (target === '/provider' || target === '/resources' || (register && target === '/'))
      ? siteURL(
          'work',
          target === '/'
            ? registrationRole === 'provider'
              ? '/provider'
              : registrationRole === 'resource'
                ? '/resources'
                : '/'
            : target,
        )
      : target;
  function resolvedDestination(selectedRole?: string) {
    return selectedRole &&
      typeof window !== 'undefined' &&
      new URL(siteURL('www')).origin === window.location.origin &&
      ['/', '/provider', '/resources'].includes(target)
      ? siteURL(
          'work',
          selectedRole === 'provider'
            ? '/provider'
            : selectedRole === 'resource'
              ? '/resources'
              : '/',
        )
      : destination;
  }
  function finishAuth() {
    const next = resolvedDestination();
    if (next.startsWith('http')) window.location.assign(next);
    else navigate(next, { replace: true });
  }
  useEffect(() => {
    if (session.data && !verifyToken && !emailChangeToken && !resetToken && !isSubmitting)
      finishAuth();
  }, [
    session.data,
    destination,
    navigate,
    verifyToken,
    emailChangeToken,
    resetToken,
    isSubmitting,
  ]);
  useEffect(() => {
    if (!verifyToken) return;
    if (verification.current?.token !== verifyToken)
      verification.current = {
        token: verifyToken,
        request: post('/auth/email/verify', { token: verifyToken }),
      };
    let active = true;
    verification.current.request
      .then(() => {
        void qc.invalidateQueries({ queryKey: ['session'] });
        if (active) setSecurityMessage('邮箱验证成功，现在可以继续使用影爸爸。');
      })
      .catch((e) => active && setError(e));
    return () => {
      active = false;
    };
  }, [verifyToken]);
  async function submit(values: AuthInput) {
    setError(null);
    try {
      let emailWarning = '';
      if (register) {
        const result = await post<{ email_warning?: string }>('/auth/register', values);
        emailWarning = result.email_warning || '';
        setMode('login');
      }
      if (wechatStage === 'bind') await weChatWebsiteLogin('bind', values);
      else await login(values.email, values.password, values.mfa_code || '');
      let next = resolvedDestination(register ? values.capability : undefined);
      if (emailWarning) {
        const url = new URL(next, window.location.origin);
        url.searchParams.set('email_delivery', 'failed');
        next = next.startsWith('http') ? url.toString() : url.pathname + url.search;
      }
      if (next.startsWith('http')) {
        window.location.assign(next);
        return;
      }
      await qc.invalidateQueries({ queryKey: ['session'] });
      navigate(next, { replace: true });
    } catch (e) {
      setError(e);
    }
  }
  async function confirmEmailChange(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (securityPending.current) return;
    securityPending.current = true;
    setSecurityBusy(true);
    setError(null);
    try {
      await post('/auth/email/change/confirm', { token: emailChangeToken });
      qc.clear();
      navigate('/login?email_changed=1', { replace: true });
      setSecurityMessage('登录邮箱已更换并验证。所有设备的旧会话已失效，请使用新邮箱登录。');
    } catch (error) {
      setError(error);
    } finally {
      securityPending.current = false;
      setSecurityBusy(false);
    }
  }
  async function startWechat() {
    if (wechatBusy) return;
    setWechatBusy(true);
    setError(null);
    const generation = ++qrGeneration.current;
    if (wechatOfficial) {
      setQROpen(true);
      setWechatQR(null);
      setQRError(null);
      setQRState('正在准备二维码…');
    }
    try {
      if (wechatOfficial) {
        const result = await post<{ qr_code_url: string; expires_at: string; login_code: string }>(
          '/auth/wechat/official/start',
          { return_to: target },
        );
        if (generation !== qrGeneration.current) {
          await post('/auth/wechat/official/cancel');
          return;
        }
        const url = new URL(result.qr_code_url);
        if (
          url.origin !== 'https://mp.weixin.qq.com' ||
          url.pathname !== '/cgi-bin/showqrcode' ||
          !Number.isFinite(Date.parse(result.expires_at)) ||
          !/^\d{6}$/.test(result.login_code)
        )
          throw new Error('二维码无效，请重新获取');
        setWechatQR(result);
        setQRNow(Date.now());
        setQRState('请用微信扫一扫');
        return;
      }
      const result = await post<{ authorize_url: string }>('/auth/wechat/start', {
        return_to: target,
      });
      const url = new URL(result.authorize_url);
      if (url.origin !== 'https://open.weixin.qq.com' || url.pathname !== '/connect/qrconnect')
        throw new Error('扫码地址无效，请重试');
      window.location.assign(url.href);
    } catch (e) {
      if (generation === qrGeneration.current) {
        if (wechatOfficial) setQRError(e);
        else setError(e);
      }
    } finally {
      setWechatBusy(false);
    }
  }
  function closeWechatQR() {
    qrGeneration.current++;
    setQROpen(false);
    setWechatQR(null);
    void post('/auth/wechat/official/cancel').catch(() => {});
  }
  async function completeWechat(e: React.FormEvent) {
    e.preventDefault();
    if (wechatBusy) return;
    setWechatBusy(true);
    setError(null);
    try {
      await weChatWebsiteLogin('complete', { mfa_code: wechatMFA });
      await qc.invalidateQueries({ queryKey: ['session'] });
      finishAuth();
    } catch (e) {
      setError(e);
    } finally {
      setWechatBusy(false);
    }
  }
  async function requestReset(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (securityPending.current) return;
    securityPending.current = true;
    setSecurityBusy(true);
    setError(null);
    setSecurityMessage('');
    const email = String(new FormData(e.currentTarget).get('email') || '');
    try {
      const result = await post<{ sent: boolean; debug_token?: string }>(
        '/auth/password-reset/request',
        { email },
      );
      setSecurityMessage(
        result.debug_token
          ? `本地开发模式重置 token：${result.debug_token}`
          : '如果该邮箱已注册，我们已经发送密码重置邮件。',
      );
    } catch (e) {
      setError(e);
    } finally {
      securityPending.current = false;
      setSecurityBusy(false);
    }
  }
  async function confirmReset(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (securityPending.current) return;
    setError(null);
    const form = new FormData(e.currentTarget);
    const password = String(form.get('password') || '');
    const confirm = String(form.get('confirm_password') || '');
    if (password !== confirm) {
      setError(new Error('两次填写的新密码不一致'));
      return;
    }
    securityPending.current = true;
    setSecurityBusy(true);
    try {
      await post('/auth/password-reset/confirm', { token: resetToken, password });
      qc.clear();
      setSecurityMessage('密码已重置，请使用新密码登录。');
      const next = new URLSearchParams({ changed: '1', returnTo: target });
      if (registrationRole) next.set('role', registrationRole);
      navigate('/login?' + next.toString(), { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      securityPending.current = false;
      setSecurityBusy(false);
    }
  }
  return (
    <div className="auth-layout">
      <div className="auth-visual">
        <Image src="/assets/hero.webp" alt="在海岸拍摄的影视创作者" eager />
        <div className="auth-visual-content">
          <span className="eyebrow" style={{ color: '#efbb82' }}>
            MAKE SOMETHING THAT MATTERS
          </span>
          <h2>
            每个好故事，
            <br />
            都值得被认真拍摄。
          </h2>
          <p>连接创意与制作，让专业的人，做擅长的事。</p>
        </div>
      </div>
      <div className="auth-main">
        <div className="auth-form">
          <span className="eyebrow">{admin ? 'OPERATIONS' : 'WELCOME TO YINGBABA'}</span>
          <h1>
            {admin
              ? '登录运营控制台'
              : resetToken
                ? '设置新密码'
                : forgot
                  ? '找回密码'
                  : register
                    ? '开启你的创作之旅'
                    : '欢迎回到影爸爸'}
          </h1>
          <p>
            {admin
              ? '使用独立的运营账号与会话访问管理平台。'
              : register
                ? '一个账号，连接客户、团队与行业资源。'
                : '从一个好想法，到一部好作品。'}
          </p>
          {securityMessage && <Notice>{securityMessage}</Notice>}
          {!admin && params.get('email_changed') === '1' && !securityMessage && (
            <Notice>邮箱已更换，请使用新邮箱重新登录。</Notice>
          )}
          {wechatStage === 'failed' && (
            <Notice>微信授权未完成或已过期，请重新扫码，也可以使用邮箱登录。</Notice>
          )}
          {wechatStage === 'bind' && (
            <Notice>
              首次扫码，请验证并绑定已有影爸爸账号。尚未注册可先创建账号，再完成绑定。
            </Notice>
          )}
          {emailChangeToken ? (
            <form className="form-stack" onSubmit={confirmEmailChange}>
              <Notice>
                确认你本人申请了更换登录邮箱。确认后，新邮箱成为登录地址，所有设备上的旧会话会失效。
              </Notice>
              <ErrorNotice error={error} />
              <Button type="submit" loading={securityBusy} className="btn-wide">
                确认验证并更换邮箱
              </Button>
            </form>
          ) : verifyToken && securityMessage && session.data ? (
            <Button
              className="btn-wide"
              onClick={() => window.location.assign(siteURL('work', '/account'))}
            >
              进入工作台
            </Button>
          ) : resetToken ? (
            <form onSubmit={confirmReset} className="form-stack">
              <Field
                label="新密码"
                htmlFor="reset-password"
                hint="至少 10 位。重置后所有设备上的旧会话都会失效。"
              >
                <input
                  id="reset-password"
                  className="input"
                  name="password"
                  type="password"
                  minLength={10}
                  maxLength={72}
                  required
                  autoComplete="new-password"
                />
              </Field>
              <Field label="再次填写新密码" htmlFor="reset-confirm-password">
                <input
                  id="reset-confirm-password"
                  className="input"
                  name="confirm_password"
                  type="password"
                  minLength={10}
                  maxLength={72}
                  required
                  autoComplete="new-password"
                />
              </Field>
              <ErrorNotice error={error} />
              <Button type="submit" loading={securityBusy} className="btn-wide">
                重置密码
              </Button>
            </form>
          ) : forgot ? (
            <form onSubmit={requestReset} className="form-stack">
              <Field label="登录邮箱" htmlFor="reset-email">
                <input
                  id="reset-email"
                  className="input"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                />
              </Field>
              <ErrorNotice error={error} />
              <Button type="submit" loading={securityBusy} className="btn-wide">
                发送重置邮件
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={securityBusy}
                onClick={() => {
                  setMode('login');
                }}
              >
                返回登录
              </Button>
            </form>
          ) : wechatStage === 'complete' ? (
            <form onSubmit={completeWechat} className="form-stack">
              <Notice>
                {wechatStatus ? '微信授权已验证，确认后进入影爸爸。' : '正在验证扫码结果…'}
              </Notice>
              {wechatStatus?.mfa_required && (
                <Field
                  label="双因素验证码"
                  htmlFor="wechat-mfa"
                  hint="填写认证器验证码或一次性恢复码。"
                >
                  <input
                    id="wechat-mfa"
                    className="input"
                    autoComplete="one-time-code"
                    value={wechatMFA}
                    onChange={(e) => setWechatMFA(e.target.value)}
                    maxLength={32}
                    required
                  />
                </Field>
              )}
              <ErrorNotice error={error} />
              <Button
                type="submit"
                loading={wechatBusy}
                disabled={!wechatStatus}
                className="btn-wide"
              >
                确认登录
              </Button>
              <Link className="link" to={`/login?returnTo=${encodeURIComponent(target)}`}>
                改用邮箱登录
              </Link>
            </form>
          ) : (
            <form onSubmit={handleSubmit(submit)} className="form-stack">
              {!register && params.get('changed') === '1' && (
                <p className="small" role="status">
                  密码已更新，请使用新密码登录。
                </p>
              )}
              {register && (
                <>
                  <Field label="你的姓名" htmlFor="name" error={errors.name?.message}>
                    <input
                      id="name"
                      className="input"
                      {...field('name')}
                      required
                      minLength={2}
                      maxLength={40}
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="企业 / 团队名称" htmlFor="organization">
                    <input
                      id="organization"
                      className="input"
                      {...field('organization')}
                      required
                      minLength={2}
                      maxLength={80}
                      autoComplete="organization"
                    />
                  </Field>
                  <Field label="主要业务身份" htmlFor="capability">
                    <Controller
                      name="capability"
                      control={control}
                      render={({ field }) => (
                        <Select id="capability" {...field} validationBehavior="aria">
                          <option value="client">我想找团队拍片</option>
                          <option value="provider">我提供影视制作服务</option>
                          <option value="resource">我提供影视资源</option>
                        </Select>
                      )}
                    />
                  </Field>
                </>
              )}
              <Field label="邮箱地址" htmlFor="email" error={errors.email?.message}>
                <input
                  id="email"
                  className="input"
                  placeholder="name@company.com"
                  {...field('email')}
                  autoComplete="email"
                  type="email"
                />
              </Field>
              <Field label="密码" htmlFor="password" error={errors.password?.message}>
                <input
                  id="password"
                  className="input"
                  placeholder="至少 10 个字符"
                  {...field('password')}
                  type="password"
                  autoComplete={register ? 'new-password' : 'current-password'}
                />
              </Field>
              {!register && (
                <Field
                  label="双因素验证码（如已启用）"
                  htmlFor="mfa-code"
                  error={errors.mfa_code?.message}
                  hint="可填写认证器 6 位验证码或一次性恢复码。"
                >
                  <input
                    id="mfa-code"
                    className="input"
                    {...field('mfa_code')}
                    autoComplete="one-time-code"
                    maxLength={32}
                  />
                </Field>
              )}
              {register && !admin && (
                <label className="auth-consent">
                  <input type="checkbox" required />
                  <span>
                    我已阅读并同意{' '}
                    <a href={siteURL('www', '/terms')} target="_blank" rel="noopener noreferrer">
                      服务条款
                    </a>
                    ，并已阅读{' '}
                    <a href={siteURL('www', '/privacy')} target="_blank" rel="noopener noreferrer">
                      隐私政策
                    </a>
                    。
                  </span>
                </label>
              )}
              <ErrorNotice error={error} />
              <Button type="submit" loading={isSubmitting} className="btn-wide">
                {register ? '创建账号' : wechatStage === 'bind' ? '验证并绑定微信' : '登录'}
                <ArrowRight size={16} />
              </Button>
            </form>
          )}
          {!admin && wechatEnabled && !register && !forgot && !resetToken && (
            <div className="wechat-login-option">
              <span className="muted small">或使用微信登录</span>
              <Button
                type="button"
                variant="secondary"
                className="btn-wide"
                loading={wechatBusy}
                disabled={isSubmitting || securityBusy}
                onClick={startWechat}
              >
                {['bind', 'complete', 'failed'].includes(wechatStage) ? '重新扫码' : '微信扫码登录'}
              </Button>
              <span className="muted small">
                {wechatOfficial
                  ? '扫码后在影爸爸服务号消息中确认登录；首次使用可绑定已有账号。'
                  : '前往微信官方授权页面，扫码后返回影爸爸。'}
              </span>
            </div>
          )}
          <Modal
            open={qrOpen}
            onOpenChange={(open) => {
              if (!open) closeWechatQR();
            }}
            title="微信扫码登录"
            description="用微信扫描二维码，核对识别码后在微信消息中确认本次登录。"
          >
            <div className="wechat-official-qr stack">
              {wechatQR ? (
                <>
                  {qrNow < new Date(wechatQR.expires_at).getTime() ? (
                    <img
                      src={wechatQR.qr_code_url}
                      alt="影爸爸服务号登录二维码"
                      width={220}
                      height={220}
                      referrerPolicy="no-referrer"
                      onError={() =>
                        setQRError(new Error('二维码图片暂时无法加载，请刷新二维码。'))
                      }
                    />
                  ) : (
                    <div className="wechat-qr-expired">二维码已过期</div>
                  )}
                  <p>
                    电脑识别码：<strong className="wechat-login-code">{wechatQR.login_code}</strong>
                  </p>
                  <p className="small muted" role="status">
                    {qrNow < new Date(wechatQR.expires_at).getTime()
                      ? qrState
                      : '请刷新二维码后重新扫码'}
                  </p>
                  <p className="small muted">
                    未关注时，请先关注影爸爸服务号以接收本次确认消息。关注本身不会完成登录，也不代表同意接收营销消息。
                  </p>
                </>
              ) : (
                <p role="status">{wechatBusy ? '正在准备二维码…' : '二维码尚未生成'}</p>
              )}
              <ErrorNotice error={qrError} />
              <Button variant="secondary" loading={wechatBusy} onClick={startWechat}>
                刷新二维码
              </Button>
            </div>
          </Modal>
          {!admin && !register && !forgot && !resetToken && wechatStage !== 'complete' && (
            <div className="auth-links">
              <span className="muted">忘记登录密码？</span>
              <button
                className="link"
                style={{ border: 0, background: 'none' }}
                onClick={() => {
                  setMode('forgot');
                }}
              >
                重置密码
              </button>
            </div>
          )}
          {!admin && !forgot && !resetToken && wechatStage !== 'complete' && (
            <div className="auth-links">
              <span className="muted">{register ? '已有账号？' : '还没有账号？'}</span>
              <button
                className="link"
                style={{ border: 0, background: 'none' }}
                onClick={() => {
                  setMode(register ? 'login' : 'register');
                }}
              >
                {register ? '直接登录' : '免费注册'}
              </button>
            </div>
          )}
          {!admin && (
            <div className="auth-terms">
              你可以随时查阅{' '}
              <a href={siteURL('www', '/terms')} className="link">
                服务条款
              </a>{' '}
              和{' '}
              <a href={siteURL('www', '/privacy')} className="link">
                隐私政策
              </a>
              。项目资料仅向有权限的组织成员开放，主动发布的公开内容除外。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
export function LoginRequired({ children }: { children: ReactNode }) {
  const session = useSession();
  const navigate = useNavigate();
  useEffect(() => {
    if (session.data === null)
      navigate('/login?returnTo=' + encodeURIComponent(location.pathname + location.search), {
        replace: true,
      });
  }, [session.data, navigate]);
  if (session.isPending) return <Loading />;
  if (session.error) return <ErrorNotice error={session.error} />;
  return session.data ? <>{children}</> : null;
}
export function Pagination({
  page,
  total,
  size,
  onPage,
  label = '分页',
}: {
  page: number;
  total: number;
  size: number;
  onPage: (n: number) => void;
  label?: string;
}) {
  if (total <= size) return null;
  return (
    <nav className="pagination" aria-label={label}>
      <Button variant="secondary" disabled={page === 1} onClick={() => onPage(page - 1)}>
        上一页
      </Button>
      <span>
        {page} / {Math.ceil(total / size)}
      </span>
      <Button variant="secondary" disabled={page * size >= total} onClick={() => onPage(page + 1)}>
        下一页
      </Button>
    </nav>
  );
}
export function Stat({
  label,
  value,
  foot,
  icon,
}: {
  label: string;
  value: ReactNode;
  foot?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="stat">
      <div className="stat-label">
        {label}
        {icon}
      </div>
      <div className="stat-number mono">{value}</div>
      {foot && <div className="stat-foot">{foot}</div>}
    </div>
  );
}
